import { WithStyles } from '@material-ui/core/styles'
import MapboxGl from 'mapbox-gl'
import React, { useContext, useState } from 'react'

import { ComponentControl } from 'src/commons/hooks/useComponentControl'

import GeoJSONLayer from './GeoJsonLayer'

export type MapEvent = (
  map: MapboxGl.Map,

  evt: React.SyntheticEvent<any>
) => void

export interface IViewport {
  center: {
    lat: number
    lng: number
  }
  zoom: number
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IGeoJsonData extends GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> {}

export interface IMapContent {
  geometry: IGeoJsonData
  id: string
  options: string
}

export interface IMapProps extends WithStyles {
  children?: React.ReactNode
  componentControl?: ComponentControl
  currentGeoJSON?: IGeoJsonData
  customAction?: React.ReactElement
  height?: number
  onBoxZoomCancel?: MapEvent
  onBoxZoomEnd?: MapEvent
  onBoxZoomStart?: MapEvent
  onClick?: MapEvent
  onContextMenu?: MapEvent
  onData?: MapEvent
  onDataLoading?: MapEvent
  onDblClick?: MapEvent
  onDrag?: MapEvent
  onDragEnd?: MapEvent
  onDragStart?: MapEvent
  onError?: MapEvent
  onMapReady?: (map: MapboxGl.Map) => void
  onMouseDown?: MapEvent
  onMouseMove?: MapEvent
  onMouseOut?: MapEvent
  onMouseUp?: MapEvent
  onMove?: MapEvent
  onMoveEnd?: MapEvent
  onMoveStart?: MapEvent
  onPitch?: MapEvent
  onPitchEnd?: MapEvent
  onPitchStart?: MapEvent
  onRemove?: MapEvent
  onRender?: MapEvent
  onResize?: MapEvent
  onRotate?: MapEvent
  onRotateEnd?: MapEvent
  onRotateStart?: MapEvent
  onSourceData?: MapEvent
  onSourceDataLoading?: MapEvent
  onStyleData?: MapEvent
  onStyleDataLoading?: MapEvent
  onStyleLoad?: MapEvent
  onTouchCancel?: MapEvent
  onTouchEnd?: MapEvent
  onTouchMove?: MapEvent
  onTouchStart?: MapEvent
  onViewportChange?: (position: IViewport) => void
  onWebGlContextLost?: MapEvent
  onWebGlContextRestored?: MapEvent
  onZoom?: MapEvent
  onZoomEnd?: MapEvent
  onZoomStart?: MapEvent
  options?: any
  showSatellite?: boolean
  showScale?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | false
  showSearchBar?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | false
  viewport?: IViewport
  width?: number
  zoomControlPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

export const MapContext = React.createContext(undefined) as React.Context<MapboxGl.Map | undefined>

export const useMap = () => {
  return useContext(MapContext)
}

export const _testState: { lastRenderGeoJsonLayersReturn: any } = { lastRenderGeoJsonLayersReturn: null }

export function useMapContent(
  init: IMapContent[]
): [
  IMapContent[],
  (id: string, options: any, geometry: IGeoJsonData) => void,
  (id: string) => void,
  (id: string) => IMapContent | undefined,
  () => void,
  () => JSX.Element[]
] {
  const [mapContent, setMapContent] = useState<IMapContent[]>(init)

  const setContent = (id: string, options: any, geometry: IGeoJsonData) => {
    setMapContent((content) =>
      content
        .filter((c) => c.id !== id)
        .concat([
          {
            id,
            options,
            geometry,
          },
        ])
    )
  }

  const removeContent = (id: string) => {
    setMapContent((c: IMapContent[]) => c.filter((content: IMapContent) => content.id !== id))
  }

  const clearContent = () => {
    setMapContent([])
  }

  const getContent = (id: string) => {
    return mapContent.find((c: IMapContent) => c.id === id)
  }

  function renderGeoJsonLayers() {
    const layers = mapContent.map((c) => <GeoJSONLayer key={c.id} geoData={c.geometry} layerOptions={c.options} />)
    _testState.lastRenderGeoJsonLayersReturn = layers
    return layers
  }

  return [mapContent, setContent, removeContent, getContent, clearContent, renderGeoJsonLayers]
}

/** Subtle fill used to overlay an area boundary (e.g. a jurisdiction) on a map for context. */
export const BOUNDARY_LAYER_OPTIONS = { layout: { visibility: 'visible' }, paint: { 'fill-opacity': 0.05 } }

export { default as GeoJSONLayer } from './GeoJsonLayer'
export { default as DrawLayer } from './DrawLayer'
export { default as MapBoxMap } from './MapBoxMap'
export { default as MapPopup } from './MapPopup'
export { default as MapActionBar } from './MapActionBar'
export { default as MapSpinner } from './MapSpinner'
export { default as MapProgressBar } from './MapProgressBar'
